import React from 'react';
import {
  Card,
  Text,
  Group,
  ActionIcon,
  Menu,
  Stack,
  SimpleGrid,
} from '@mantine/core';
import {
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconCalendar,
  IconBuilding,
  IconClipboardList,
  IconChecklist,
  IconFileReport,
  IconMapPin,
  IconHome,
  IconFolders,
  IconCopy,
} from '@tabler/icons-react';
import { AuditorDisplay } from '../AuditorDisplay';
import { ImageThumbnail } from '../ImageThumbnail';
import { AuditWithDetails } from '../types';

interface AuditCardProps {
  audit: AuditWithDetails;
  onEdit: (audit: AuditWithDetails) => void;
  onDelete: (audit: AuditWithDetails) => void;
  onShowReport: (audit: AuditWithDetails) => void;
  onDuplicate: (audit: AuditWithDetails) => void;
}

export const AuditCard: React.FC<AuditCardProps> = ({
  audit,
  onEdit,
  onDelete,
  onShowReport,
  onDuplicate
}) => {
  const direktionName = audit.expand?.dienststelle?.expand?.direktion?.name || 'Keine Direktion';
  const dienststelleName = audit.expand?.dienststelle?.name || 'Keine Dienststelle';
  const themaName = audit.expand?.bereich?.name || 'Kein Bereich / Abteilung';
  const adresseText = audit.expand?.bereich?.adresse || 'Keine Adresse';

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group justify="space-between" mb="xs">
        <Text fw={500} size="lg">
          Audit Nr. {audit.auditnummer}
        </Text>
        <Menu shadow="md" width={200}>
          <Menu.Target>
            <ActionIcon variant="subtle">
              <IconDotsVertical size={16} />
            </ActionIcon>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconEdit size={14} />}
              onClick={() => onEdit(audit)}
            >
              Bearbeiten
            </Menu.Item>
            <Menu.Item
              leftSection={<IconCopy size={14} />}
              onClick={() => onDuplicate(audit)}
            >
              Audit Duplizieren
            </Menu.Item>
            <Menu.Item
              leftSection={<IconFileReport size={14} />}
              onClick={() => onShowReport(audit)}
            >
              Bericht
            </Menu.Item>
            <Menu.Item
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={() => onDelete(audit)}
            >
              Löschen
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      <Stack gap={8} mb="md">
        <Group gap={8}>
          <IconCalendar size={16} />
          <Text size="sm">{new Date(audit.auditdatum).toLocaleDateString()}</Text>
        </Group>

        <Group gap={8}>
          <IconBuilding size={16} />
          <Text size="sm" lineClamp={1}>
            {direktionName}
          </Text>
        </Group>

        <Group gap={8}>
          <IconHome size={16} />
          <Text size="sm" lineClamp={1}>
            {dienststelleName}
          </Text>
        </Group>

        <Group gap={8}>
          <IconFolders size={16} />
          <Text size="sm" lineClamp={1}>
            {themaName}
          </Text>
        </Group>

        <Group gap={8}>
          <IconMapPin size={16} />
          <Text size="sm" lineClamp={2}>
            {adresseText}
          </Text>
        </Group>

        <AuditorDisplay auditors={audit.expand?.auditor || []} />

        <Group gap={8}>
          <IconClipboardList size={16} />
          <Text size="sm">
            {audit.massnahmen?.length || 0} Maßnahmen
          </Text>
        </Group>

        <Group gap={8}>
          <IconChecklist size={16} />
          <Text size="sm">
            {audit.kontrollpunkteCount || 0} Kontrollstellen
          </Text>
        </Group>
      </Stack>

      {audit.massnahmen && audit.massnahmen.length > 0 && (
        <SimpleGrid cols={3} spacing={8}>
          {audit.massnahmen
            .filter(massnahme => massnahme.anhaenge?.length > 0)
            .map((massnahme) => (
              <ImageThumbnail key={massnahme.id} massnahme={massnahme} />
            ))}
        </SimpleGrid>
      )}
    </Card>
  );
};
